import { FC, useEffect, useState, useRef } from "react";
import { Header } from "./Header";
import { Row, Col } from "react-bootstrap";
import { LeftSection, type LeftSectionHandle } from "./LeftSection";
import { RightSection } from "./RightSection";
import { SeoGraderModel } from "../types";
import {
  checkH1InContent,
  countWordsInContent,
  countImagesInContent,
  countLinksInContent,
  checkKeywordInFirstParagraph,
  getMetaDescriptionContent,
  checkKeywordInTitle,
  checkKeywordAtBeginning,
  checkPageTitleLength,
  checkMetaDescriptionExists,
  checkKeywordInH1,
  calculateKeywordDensity,
  checkKeywordInImageName,
  checkKeywordInImageAlt,
  calculateAdditionalKeywordDensity,
  checkAdditionalKeywordPresence,
} from "../util";
import { Keywords } from "./Keywords";
import { Box2 } from "./SeocontentStyle";
type Props = {};

const sampleData = {
  content: "",
  focusKeyword: "",
  metaDescription: "",
  summary: {
    score: 0,
  },
  tabs: {
    seoOptimization: {
      sections: [
        {
          key: "pageTitle",
          title: "Page Title Score",
          seoItems: [
            {
              key: "pageTitlePresense",
              text: "You should add a Page Title",
              grade: undefined,
            },
            {
              key: "focusKeywordAppear",
              grade: undefined,
              text: 'The focus keyword "N/A" doesn\'t appear in the Page Title',
            },
            {
              key: "putFocusKeyword",
              grade: undefined,
              text: "Put the focus keyword at the beginning of the Page Title",
            },
            {
              key: "pageTitleAvailable",
              text: "The Page Title is too short, 60 characters available. (0 of 60 characters used)",
              grade: undefined,
            },
          ],
        },
        {
          key: "pageMetaData",
          title: "Page Metadata Score",
          seoItems: [
            {
              key: "addMetaDescription",
              grade: undefined,
              text: "You should add a Meta description",
            },
            {
              key: "metaFocusKeyword",
              grade: undefined,
              text: "The focus keyword doesn't appear in the Meta description",
            },
            {
              key: "metaDescriptionAvailable",
              grade: undefined,
              text: "The Meta description is too short, 160 characters available. (0 of 160 characters used)",
            },
          ],
        },
        {
          key: "ContentScore",
          title: "Content Score",
          seoItems: [
            {
              key: "addContentScore",
              grade: undefined,
              text: "You should add a H1",
            },
            {
              key: "keywordPresense",
              grade: undefined,
              text: 'The focus keyword "N/A" doesn\'t appear in the H1',
            },
            {
              key: "addText",
              grade: undefined,
              text: "You should add text",
            },
            {
              key: "addTextWords",
              grade: undefined,
              text: "Your text doesn't contain enough words, a minimum of 300 words is recommended",
            },
            {
              key: "keywordParagraph",
              grade: undefined,
              text: "The focus keyword doesn't appear in first paragraph of the text",
            },
            {
              key: "keywordDensity",
              grade: undefined,
              text: "You should use the focus keyword more often, to improve the keyword density (0%)",
            },
            {
              key: "additionalKeywordPresence",
              text: "Each additional keyword should be atleast one time in content",
              grade: undefined,
            },
            {
              key: "additionalKeywordDensity",
              grade: undefined,
              text: "You should use the additional keyword more often, to improve the keyword density (0%)",
            },
            {
              key: "mediaDiversification",
              grade: undefined,
              text: "You should add an image",
            },
            {
              key: "imageNameKeyword",
              grade: undefined,
              text: 'The focus keyword "N/A" doesn\'t appear in the image name',
            },
            {
              key: "imageAltKeyword",
              grade: undefined,
              text: 'The focus keyword "N/A" doesn\'t appear in the image Alt tag',
            },
            {
              key: "externalLinks",
              grade: undefined,
              text: "Add relevant links to improve user experience and internal link structure",
            },
          ],
        },
      ],
    },
  },
} as SeoGraderModel;

export const SeoContentGraderAnalyzer: FC<Props> = () => {
  const leftSectionRef = useRef<LeftSectionHandle>(null);
  const [seoGrader, setSeoGrader] = useState<SeoGraderModel>(sampleData);
  const [content, setContent] = useState<string>("");
  const [content2, setContent2] = useState<string>("");
  const [additionalKeyword, setAdditionalKeyword] = useState("");
  const [selectedWordCount, setSelectedWordCount] = useState<number>(0);
  const [originalSeoItems] = useState<SeoGraderModel>(sampleData);
  const [averageScore, setAverageScore] = useState<number>(0);
  const [metrics, setMetrics] = useState({
    wordCount: 0,
    headingCount: 0,
    linkCount: 0,
    termCount: 0,
  });

  const isEditorEmpty = (html: string) => {
    const plainText = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    return plainText.length === 0;
  };

  useEffect(() => {
    const editorIsEmpty = isEditorEmpty(content);

    if (editorIsEmpty) {
      setAverageScore(0);
      setMetrics({
        wordCount: 0,
        headingCount: 0,
        linkCount: 0,
        termCount: 0,
      });

      setSeoGrader((prev) => ({
        ...prev,
        content: "",
        tabs: JSON.parse(JSON.stringify(sampleData.tabs)),
      }));

      return;
    }
    const wordCount = selectedWordCount;
    // const headingCount = (content.match(/<h1\b[^>]*>(.*?)<\/h1>/gi) || [])
    //   .length;
    const headingCount = (content.match(/<h[1-6]\b[^>]*>(.*?)<\/h[1-6]>/gi) || [])
      .length;
    const linkCount = countLinksInContent(content);
    const termCount = seoGrader.focusKeyword
      ? (content.match(new RegExp(seoGrader.focusKeyword, "gi")) || []).length
      : 0;

    // const updatedSeoGrader = { ...seoGrader };
    const updatedSeoGrader = JSON.parse(
      JSON.stringify(seoGrader)
    ) as SeoGraderModel;
    const { focusKeyword } = seoGrader;

    updatedSeoGrader.focusKeyword = seoGrader.focusKeyword;
    updatedSeoGrader.content = content;

    let totalScore = 0;
    let sectionCount = 0;

    // Iterate over each section to check grades and update accordingly
    updatedSeoGrader.tabs.seoOptimization.sections.forEach((section) => {
      section.seoItems.forEach((seoItem) => {
        seoItem.text = seoItem.text.replace(
          /N\/A/g,
          seoGrader.focusKeyword.trim() || "N/A"
        );

        if (section.key === "pageTitle") {
          // Page Title Section
          if (seoItem.key === "pageTitlePresense") {
            if (checkH1InContent(content)) {
              seoItem.grade = "Excellent";
              seoItem.text = "You've added Page Title (H1)";
            } else {
              seoItem.grade = "Poor";
              seoItem.text = "You should add a Page Title";
            }
          }

          if (seoItem.key === "focusKeywordAppear") {
            const keyword = seoGrader.focusKeyword; // Use focusKeyword from seoGrader
            if (keyword && checkKeywordInTitle(content, keyword)) {
              seoItem.grade = "Excellent";
              seoItem.text = `The focus keyword "${keyword}" appears in the Page Title`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `The focus keyword "${keyword || "N/A"
                }" doesn't appear in the Page Title`;
            }
          }
          if (seoItem.key === "putFocusKeyword") {
            const keyword = seoGrader.focusKeyword; // Get the focus keyword
            if (keyword) {
              const hasKeywordAtStart = checkKeywordAtBeginning(
                content,
                keyword
              );
              seoItem.grade = hasKeywordAtStart ? "Excellent" : "Poor";
              seoItem.text = hasKeywordAtStart
                ? `The focus keyword "${keyword}" is placed at the beginning of the Page Title`
                : `The focus keyword "${keyword}" is not at the beginning of the Page Title`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text =
                "Put the focus keyword at the beginning of the Page Title";
            }
          }
          if (seoItem.key === "pageTitleAvailable") {
            const { length, grade } = checkPageTitleLength(content);
            const maxRecommended = 60;
            const minRecommended = 30;

            seoItem.grade = grade;

            if (length === 0) {
              seoItem.text = `No page title found (Recommended ${minRecommended}-${maxRecommended} characters)`;
            } else if (length < minRecommended) {
              seoItem.text = `Page title too short (${length}/${maxRecommended}). Add ${minRecommended - length
                } more characters.`;
            } else if (length > maxRecommended) {
              seoItem.text = `Page title too long (${length}/${maxRecommended}). Remove ${length - maxRecommended
                } characters.`;
            } else {
              seoItem.text = `Good page title length (${length}/${maxRecommended} characters)`;
            }
          }
        }

        if (section.key === "pageMetaData") {
          // Page Metadata Section
          if (seoItem.key === "addMetaDescription") {
            const hasMetaDescription =
              seoGrader.metaDescription?.trim().length > 0;
            seoItem.grade = hasMetaDescription ? "Excellent" : "Poor";
            seoItem.text = hasMetaDescription
              ? "You've added a Meta description"
              : "You should add a Meta description";
          }

          if (seoItem.key === "metaFocusKeyword") {
            const metaDesc = seoGrader.metaDescription?.trim() || "";
            const focusKeyword = seoGrader.focusKeyword?.trim();

            // Handle empty states first
            if (!focusKeyword) {
              seoItem.grade = "Poor";
              seoItem.text =
                "Focus keyword not set - cannot check meta description";
            } else if (!metaDesc) {
              seoItem.grade = "Poor";
              seoItem.text = `The focus keyword "${focusKeyword}" doesn't appear in the Meta description`;
            } else {
              const hasKeyword = metaDesc
                .toLowerCase()
                .includes(focusKeyword.toLowerCase());
              seoItem.grade = hasKeyword ? "Excellent" : "Poor";
              seoItem.text = hasKeyword
                ? `The focus keyword "${focusKeyword}" appears in the Meta description`
                : `The focus keyword "${focusKeyword}" doesn't appear in the Meta description`;
            }
          }

          if (seoItem.key === "metaDescriptionAvailable") {
            const metaDesc = seoGrader.metaDescription?.trim() || "";
            const descLength = metaDesc.length;
            const minLength = 120; // Recommended minimum
            const maxLength = 160; // Recommended maximum

            seoItem.text =
              descLength === 0
                ? "No meta description found"
                : descLength < minLength
                  ? `Too short (${descLength}/${maxLength}). Add ${minLength - descLength
                  } more characters.`
                  : descLength > maxLength
                    ? `Too long (${descLength}/${maxLength}). Remove ${descLength - maxLength
                    } characters.`
                    : `Good length (${descLength}/${maxLength} characters)`;

            seoItem.grade =
              descLength >= minLength && descLength <= maxLength
                ? "Excellent"
                : "Poor";
          }
        }

        if (section.key === "ContentScore") {
          // Content Section
          if (seoItem.key === "addContentScore") {
            if (checkH1InContent(content)) {
              seoItem.grade = "Excellent";
              seoItem.text = "You've added a H1";
            } else {
              seoItem.grade = "Poor";
              seoItem.text = "You should add a H1";
            }
          }

          if (seoItem.key === "addText") {
            if (content.trim().length > 0) {
              seoItem.grade = "Excellent";
              seoItem.text = "You've added text to the document";
            } else {
              seoItem.grade = "Poor";
              seoItem.text = "You should add text";
            }
          }

          if (seoItem.key === "addTextWords") {
            const wordCount = selectedWordCount;
            if (wordCount >= 300) {
              seoItem.grade = "Excellent";
              seoItem.text = `Your text contains (${wordCount}) words`; // Update dynamic text
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `Your text doesn't contain enough words, a minimum of 300 words is recommended`;
            }
          }

          if (seoItem.key === "mediaDiversification") {
            const imageCount = countImagesInContent(content);

            if (imageCount > 0) {
              seoItem.grade = "Excellent";
              seoItem.text = "You've added an image";
            } else {
              seoItem.grade = "Poor";
              seoItem.text = "You should add an image";
            }
          }

          if (seoItem.key === "externalLinks") {
            const linkCount = countLinksInContent(content);

            if (linkCount > 0) {
              seoItem.grade = "Excellent";
              seoItem.text = `You've added (${linkCount})Links to the document`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text =
                "Add relevant links to improve user experience and internal link structure";
            }
          }

          if (seoItem.key === "keywordPresense") {
            const keyword = seoGrader.focusKeyword;
            const keywordInH1 = checkKeywordInH1(content, keyword);

            // First handle empty keyword case
            if (!keyword) {
              seoItem.grade = "Poor";
              seoItem.text =
                'The focus keyword "N/A" doesn\'t appear in the H1 ';
              return;
            }

            // Now handle keyword exists case
            seoItem.grade = keywordInH1 ? "Excellent" : "Poor";
            seoItem.text = keywordInH1
              ? `The focus keyword "${keyword}" appears in the H1`
              : `The focus keyword "${keyword}" doesn't appear in the H1`;

            // Remove the separate replace logic
          }

          if (seoItem.key === "keywordParagraph") {
            if (checkKeywordInFirstParagraph(content, seoGrader.focusKeyword)) {
              seoItem.grade = "Excellent";
              seoItem.text = `The focus keyword "${seoGrader.focusKeyword}" appears in the first paragraph`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `The focus keyword doesn't appear in first paragraph of the text`;
            }
          }

          if (seoItem.key === "keywordDensity") {
            const density = calculateKeywordDensity(
              content,
              seoGrader.focusKeyword
            );
            seoItem.text = `Keyword density: ${density}%`;

            if (density >= 0.5 && density <= 2) {
              seoItem.grade = "Excellent";
              seoItem.text = `Excellent focus keyword density (${density}%)`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text =
                density === 0
                  ? "You should use the focus keyword more often to improve keyword density (0%)"
                  : `Adjust focus keyword density (current: ${density}%). Ideal range: 0.5-2.0%.`;
            }
          }

          if (seoItem.key === "additionalKeywordPresence") {
            const isPresent = checkAdditionalKeywordPresence(
              content,
              additionalKeyword
            );

            if (!additionalKeyword) {
              seoItem.grade = "Poor";
              seoItem.text =
                "Please enter an additional keyword to check its presence";
            } else if (isPresent) {
              seoItem.grade = "Excellent";
              seoItem.text = `Additional keyword "${additionalKeyword}" found in content`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `Additional keyword "${additionalKeyword}" not found. Add it at least once.`;
            }
          }

          if (seoItem.key === "additionalKeywordDensity") {
            const density = calculateAdditionalKeywordDensity(
              content,
              additionalKeyword
            );

            if (!additionalKeyword) {
              seoItem.grade = "Poor";
              seoItem.text =
                "Please enter an additional keyword to analyze its density";
            } else if (density >= 0.5 && density <= 1) {
              seoItem.grade = "Excellent";
              seoItem.text = `Excellent additional keyword density (${density}%)`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text =
                density === 0
                  ? `The additional keyword "${additionalKeyword}" is not used. Add it to improve density.`
                  : `Adjust additional keyword density (current: ${density}%). Ideal range: 0.5-1%.`;
            }
          }

          // Add this block for image name check
          if (seoItem.key === "imageNameKeyword") {
            const hasKeywordInImage = checkKeywordInImageName(
              content,
              seoGrader.focusKeyword
            );

            if (hasKeywordInImage) {
              seoItem.grade = "Excellent";
              seoItem.text = `The focus keyword "${seoGrader.focusKeyword}" appears in image names`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `The focus keyword doesn't appear in image names`;
            }
          }

          if (seoItem.key === "imageAltKeyword") {
            const hasKeywordInAlt = checkKeywordInImageAlt(
              content,
              seoGrader.focusKeyword
            );

            if (hasKeywordInAlt) {
              seoItem.grade = "Excellent";
              seoItem.text = `The focus keyword "${seoGrader.focusKeyword}" appears in image Alt tags`;
            } else {
              seoItem.grade = "Poor";
              seoItem.text = `The focus keyword doesn't appear in image Alt tags`;
            }
          }
        }
      });

      section.sectionScore =
        (section.seoItems.filter((x) => x.grade === "Excellent").length /
          section.seoItems.length) *
        100;

      section.sectionScore = Math.round(section.sectionScore);

      totalScore += section.sectionScore;
      sectionCount++;
    });

    setAverageScore(
      sectionCount > 0 ? Math.round(totalScore / sectionCount) : 0
    );
    setMetrics({
      wordCount,
      headingCount,
      linkCount,
      termCount,
    });

    setSeoGrader(updatedSeoGrader);
  }, [
    seoGrader.focusKeyword,
    content,
    originalSeoItems,
    additionalKeyword,
    seoGrader.metaDescription,
  ]);

  return (
    <Box2>
      <Header />
      <Row>
        <Col lg={7}>
          <LeftSection
            ref={leftSectionRef}
            content={content}
            setContent={setContent}
            setSelectedWordCount={setSelectedWordCount} // Passing the setter to LeftSection
          />
        </Col>
        <Col lg={5}>
          <RightSection
            seoGrader={seoGrader}
            leftSectionRef={leftSectionRef}
            setSeoGrader={setSeoGrader}
            averageScore={averageScore}
            wordCount={metrics.wordCount}
            headingCount={metrics.headingCount}
            linkCount={metrics.linkCount}
            termCount={metrics.termCount}
            additionalKeyword={additionalKeyword}
            setAdditionalKeyword={setAdditionalKeyword}
            content={content}
          />
        </Col>
      </Row>
    </Box2>
  );
};
